import React, { Fragment, FunctionComponent, useState } from 'react';

import styles from './LinkedinPostCard.module.scss';
import PostCardTopBar from '../PostCardTopBar';
import UserInformation from '../UserInformation';
import PostCardTextContent from '../PostCardTextContent';
import { checkTranslated } from 'app/utils/helpers';
import {
  useHandleNavigateInfluencerOrEntity,
  useOpenModalToAddUserAsInfluencer,
} from 'app/utils/check-influencer-user';
import { storeInfluencerAsLinkedin } from 'influencers/Requests';
import { _t } from 'app/utils/i18n';
import LinkedinPostStatusbar from '../LinkedinPostStatusbar';
import TwitterTweetCardTopicIcons from '../TwitterTweetCardTopicIcons';
import DateLocation from '../DateLocation';
import LinkedinPostCardMedia from '../LinkedinPostCardMedia';
import TweetCardContent from '../TweetCardContent';
import { path } from 'app/utils/path';

interface Props {
  data: any;
  compId?: string;
  isReport?: boolean;
  post_type?: 'negative' | 'positive' | 'neutral' | 'all';
  isTopicCard?: boolean;
  topic_id?: number;
  isContentClickable?: boolean;
}

const LinkedinPostCard: FunctionComponent<Props> = ({
  data,
  compId,
  isReport,
  post_type,
  isTopicCard,
  topic_id,
  isContentClickable,
}) => {
  const [isTranslated, setIsTranslated] = useState(false);
  const pathname = location?.pathname?.split('/')?.[1];
  const [openModalToAddUserAsInfluencer] = useOpenModalToAddUserAsInfluencer(
    storeInfluencerAsLinkedin,
  );
  const [handleNavigateInfluencerOrEntity] =
    useHandleNavigateInfluencerOrEntity();
  return (
    <div
      id={`${compId}-LinkedinPostCard-${data.id}`}
      className={`${styles.LinkedinPostCard} post_card_url`}
      data-type={data?.post_url}
    >
      <div className={styles.LinkedinPostCard_Container}>
        {!isReport && (
          <PostCardTopBar
            platformUrl={data?.comment_url || data?.post_url}
            platform={data?.platform}
            postId={data?.id}
            compId={`${compId}-LinkedinPostCard-${data.id}`}
            translationToggle={() => setIsTranslated((prev) => !prev)}
            postLanguages={data?.languages}
            content={data?.post || data?.content || data?.text}
            showReplyIcon={data?.is_reply}
          />
        )}
        <div
          className={`${styles.LinkedinPostCard_Container_content} post_card_url`}
          data-type={data?.url}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              justifyContent: 'space-between',
            }}
          >
          <div style={{width:'100%'}}>
              <UserInformation
                profileImage={data?.influencer?.image}
                username={data?.username}
                name={
                  checkTranslated(
                    data?.influencer?.name?.original,
                    data?.influencer?.name?.arabic,
                  ) || data?.username
                }
                bio={data?.profile_headline}
                isSmallerSize={
                  pathname === 'influencers' || pathname === 'entities'
                }
                handleInfluencerOrEntity={() =>
                  handleNavigateInfluencerOrEntity(data?.influencer)
                }
                postType={post_type}
                isClickable={!isReport}
                isUserAdded={data?.influencer?.influencer_id !== 0}
                openModalToAddInfluencer={() =>
                  openModalToAddUserAsInfluencer(
                    data?.username,
                    data?.url,
                    _t('hashtags:open_in_linkedin'),
                  )
                }
              />
              <PostCardTextContent
                content={data?.post || data?.content || data?.text}
                isReport={isReport}
                isTranslated={isTranslated}
                detailsUrl={`${path.posts.LINKEDIN_POST_DETAILS_URI}/${data?.id}`}
                isContentClickable={isContentClickable}
              />
            </div>
            {isTopicCard && topic_id && (
              <TwitterTweetCardTopicIcons
                keywords_count={data?.keywords_count}
                topic_id={topic_id}
                item_id={data?.id}
                match_percentage={data?.match_percentage}
                onItemClickAction={() => {}}
              />
            )}
          </div>
          {/*temp code to handle the doc and article media types*/}
          {data?.attachments && data?.attachments
            ?.filter(
              (i: { type: string,url:string }) => i.type !== 'IMAGE' && i.type !== 'VIDEO' && i.url,
            )
            .map((i: any, index: number) => (
              <Fragment>
                {!!index && ' | '}
                <a
                  style={{
                    display: 'inline-block',
                    marginBottom: '0.5rem',
                    textDecoration: 'underline',
                    textTransform: 'capitalize',
                  }}
                  href={i.url}
                  target="_blank"
                >
                  {_t(`basic:${i.type.toLowerCase()}`)}
                </a>
              </Fragment>
            ))}
          {data?.attachments &&
            data?.attachments?.filter(
              (i: { type: string }) => i.type === 'IMAGE' || i.type === 'VIDEO',
            ).length !== 0 && (
              <LinkedinPostCardMedia
                attachments={data?.attachments?.filter(
                  (i: { type: string }) =>
                    i.type === 'IMAGE' || i.type === 'VIDEO',
                )}
              />
            )}
          {data?.parent_comment &&
            Object.keys(data?.parent_comment).length !== 0 &&
            data?.parent_comment?.id !== 0 && (
              <TweetCardContent
                parentTweetData={data?.parent_comment}
                openModalToAddInfluencer={(url, username) =>
                  openModalToAddUserAsInfluencer(
                    username,
                    url,
                    _t('influencers:open_in_linkedin'),
                  )
                }
                handleInfluencer={(influencer) =>
                  handleNavigateInfluencerOrEntity(influencer)
                }
                isQuote
                isInstagram
              />
            )}
          <DateLocation
            created_at={data?.published_at}
            location={data?.location || data?.country?.name}
          />
          {!data?.is_reply &&
          (data?.comments_count ||
            data?.total_reactions ||
            data?.repost_count ||
            data?.replies_count) ? (
            <LinkedinPostStatusbar
              comments_count={data?.comments_count}
              total_reactions={data?.total_reactions}
              repost_count={data?.repost_count}
              replies_count={data?.replies_count}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkedinPostCard;
